/**
 * Langue de l'interface et traduction (dictionnaire @agora/shared, 6 langues).
 * Avant connexion : dernière langue utilisée sur ce poste, sinon celle du navigateur, sinon le français.
 * Après connexion : langue des préférences, sinon langue de la filiale par défaut, sinon navigateur (voir SocleProvider).
 */
import React from 'react';
import { LANGUE_PAR_DEFAUT, LOCALE_DE_LANGUE, estLangue, libelle, traduire, type CleTraduction, type Langue } from '@agora/shared';

const CLE_LANGUE = 'agora.langue';

export function langueNavigateur(): Langue | undefined {
  for (const l of navigator.languages || [navigator.language]) {
    const court = (l || '').slice(0, 2).toLowerCase();
    if (estLangue(court)) return court;
  }
  return undefined;
}

function langueInitiale(): Langue {
  try {
    const l = localStorage.getItem(CLE_LANGUE);
    if (estLangue(l)) return l;
  } catch { /* ignoré */ }
  return langueNavigateur() || LANGUE_PAR_DEFAUT;
}

/**
 * Traduction d'une clé TYPÉE (CleTraduction : clés de fr.json) – une clé inconnue est une erreur de compilation.
 * Clé construite à l'exécution : t(cleLibre('erreurs', code)).
 */
export type Traduire = (cle: CleTraduction, variables?: Record<string, string | number>) => string;

interface ContexteLangue {
  langue: Langue;
  locale: string;
  t: Traduire;
  /** Libellé d'un nœud du catalogue (métier, module, sous-module, dimension). */
  lib: (id: string) => string;
  definirLangue: (l: Langue) => void;
}

const Ctx = React.createContext<ContexteLangue | null>(null);

export function LangueProvider({ children }: { children: React.ReactNode }) {
  const [langue, setLangue] = React.useState<Langue>(langueInitiale);
  const definirLangue = React.useCallback((l: Langue) => {
    setLangue(l);
    try { localStorage.setItem(CLE_LANGUE, l); } catch { /* ignoré */ }
  }, []);
  React.useEffect(() => { document.documentElement.lang = langue; }, [langue]);
  const valeur = React.useMemo<ContexteLangue>(() => ({
    langue, locale: LOCALE_DE_LANGUE[langue], definirLangue,
    t: (cle, variables) => traduire(langue, cle, variables),
    lib: (id) => libelle(langue, id),
  }), [langue, definirLangue]);
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

/** Traduction et langue courantes : `const { t, lib, langue, locale } = useT();` */
export function useT(): ContexteLangue {
  const c = React.useContext(Ctx);
  if (!c) throw new Error('useT hors de LangueProvider');
  return c;
}
